<template>
  <div class="item-release-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('itemReleaseManage.list.title')" :subtitle="$t('itemReleaseManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddItemReleaseModal">
          {{ $t('itemReleaseManage.button.applyRelease') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="itemReleaseManageInfo.conditions.typeId" clearable class="w-full"
            :placeholder="$t('itemReleaseManage.placeholder.selectReleaseType')">
            <el-option v-for="item in itemReleaseManageInfo.itemReleaseTypes" :key="item.typeId" :label="item.typeName"
              :value="item.typeId" :disabled="item.state !== 'C'" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="itemReleaseManageInfo.conditions.applyCompany" clearable class="w-full"
            :placeholder="$t('itemReleaseManage.placeholder.selectApplyCompany')"
            @keyup.enter.native="_queryItemReleaseMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="itemReleaseManageInfo.conditions.applyPerson" clearable class="w-full"
            :placeholder="$t('itemReleaseManage.placeholder.selectApplyPerson')"
            @keyup.enter.native="_queryItemReleaseMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="itemReleaseManageInfo.conditions.idCard" clearable class="w-full"
            :placeholder="$t('itemReleaseManage.placeholder.selectIdCard')"
            @keyup.enter.native="_queryItemReleaseMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="itemReleaseManageInfo.conditions.applyTel" clearable class="w-full"
            :placeholder="$t('itemReleaseManage.placeholder.selectPhone')"
            @keyup.enter.native="_queryItemReleaseMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="itemReleaseManageInfo.conditions.state" clearable class="w-full"
            :placeholder="$t('itemReleaseManage.placeholder.selectStatus')">
            <el-option :label="$t('itemReleaseManage.status.waiting')" value="W" />
            <el-option :label="$t('itemReleaseManage.status.processing')" value="D" />
            <el-option :label="$t('itemReleaseManage.status.completed')" value="C" />
            <el-option :label="$t('itemReleaseManage.status.failed')" value="F" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryItemReleaseMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetItemReleaseMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listItemReleases(page.current, page.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('itemReleaseManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="itemReleaseManageInfo.itemReleases" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('itemReleaseManage.page.empty')" @retry="_listItemReleases(page.current, page.size)">
        <el-table-column prop="irId" :label="$t('itemReleaseManage.table.orderNo')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.irId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="typeName" :label="$t('itemReleaseManage.table.releaseType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.typeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="applyCompany" :label="$t('itemReleaseManage.table.applyCompany')" align="center"
          min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.applyCompany || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="applyPerson" :label="$t('itemReleaseManage.table.applyPerson')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.applyPerson || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('itemReleaseManage.table.idCard')" align="center" min-width="120"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="applyTel" :label="$t('itemReleaseManage.table.phone')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.applyTel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="passTime" :label="$t('itemReleaseManage.table.passTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.passTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('itemReleaseManage.table.items')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.amount }}</span>
            (
            <el-link type="primary" @click="viewResName(scope.row)">
              {{ $t('itemReleaseManage.button.viewItems') }}
            </el-link>)
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('itemReleaseManage.table.status')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName"
              :type="getStateType(scope.row.state)" />
          </template>
        </el-table-column>
        <el-table-column prop="carNum" :label="$t('itemReleaseManage.table.carNumber')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.carNum || $t('common.none') }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="200">
          <template slot-scope="scope">
            <el-button size="mini" @click="_openDetail(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
            <el-button size="mini" type="primary" v-if="scope.row.state != 'C'" @click="_openEditItemReleaseModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="danger" @click="_openDeleteItemReleaseModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 组件 -->
    <edit-item-release ref="editItemRelease" @success="handleSuccess" />
    <delete-item-release ref="deleteItemRelease" @success="handleSuccess" />
    <view-item-release-res ref="viewItemReleaseRes" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listItemRelease, listItemReleaseType } from '@/api/work/itemReleaseManageApi'
import DeleteItemRelease from '@/components/work/deleteItemRelease'
import ViewItemReleaseRes from '@/components/work/viewItemReleaseRes'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ItemReleaseManageList',
  components: {
    DeleteItemRelease,
    ViewItemReleaseRes,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityId: '',
      itemReleaseManageInfo: {
        itemReleases: [],
        total: 0,
        records: 1,
        moreCondition: false,
        irId: '',
        itemReleaseTypes: [],
        conditions: {
          typeId: '',
          applyCompany: '',
          applyPerson: '',
          idCard: '',
          applyTel: '',
          state: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.itemReleaseManageInfo.conditions.communityId = this.communityId
    this._listItemReleases(this.page.current, this.page.size)
    this._listItemReleaseTypes()
  },
  methods: {
    async _listItemReleases(page, rows) {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page,
          row: rows,
          ...this.itemReleaseManageInfo.conditions
        }
        const { data, total } = await listItemRelease(params)
        this.itemReleaseManageInfo.itemReleases = data
        this.page.total = total
      } catch (error) {
        console.error('获取物品放行列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('itemReleaseManage.page.fetchError')
      } finally {
        this.loading = false
      }
    },
    async _listItemReleaseTypes() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.communityId
        }
        const { data } = await listItemReleaseType(params)
        this.itemReleaseManageInfo.itemReleaseTypes = data
      } catch (error) {
        console.error('获取放行类型失败:', error)
      }
    },
    _openAddItemReleaseModal() {
      this.$router.push('/views/work/addItemReleaseView')
    },
    _openEditItemReleaseModel(item) {
      this.$router.push(`/views/work/editItemReleaseView?irId=${item.irId}&flowId=${item.flowId}`)
    },
    _openDeleteItemReleaseModel(item) {
      this.$refs.deleteItemRelease.open(item)
    },
    _queryItemReleaseMethod() {
      this.page.current = 1
      this._listItemReleases(this.page.current, this.page.size)
    },
    _resetItemReleaseMethod() {
      this.itemReleaseManageInfo.conditions = {
        typeId: '',
        applyCompany: '',
        applyPerson: '',
        idCard: '',
        applyTel: '',
        state: '',
        communityId: this.communityId
      }
      this.page.current = 1
      this._listItemReleases(this.page.current, this.page.size)
    },
    _moreCondition() {
      this.itemReleaseManageInfo.moreCondition = !this.itemReleaseManageInfo.moreCondition
    },
    viewResName(item) {
      this.$refs.viewItemReleaseRes.open(item)
    },
    _openDetail(item) {
      this.$router.push(`/views/work/itemReleaseDetail?irId=${item.irId}&flowId=${item.flowId}`)
    },
    handleSuccess() {
      this._listItemReleases(this.page.current, this.page.size)
    },
    getStateType(state) {
      switch (state) {
        case 'W': return 'warning'
        case 'D': return 'primary'
        case 'C': return 'success'
        case 'F': return 'danger'
        default: return 'muted'
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listItemReleases(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listItemReleases(val, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.item-release-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
